import { ref } from 'vue'

export interface DialogOptions {
  type: 'alert' | 'confirm' | 'prompt'
  title?: string
  message: string
  confirmText?: string
  cancelText?: string
  defaultValue?: string
  onOk?: (val?: any) => void
  onCancel?: () => void
  resolve: (value: any) => void
}

const currentDialog = ref<DialogOptions | null>(null)
const isOpen = ref(false)
const inputValue = ref('')

export function useDialog() {
  function confirm(
    message: string,
    titleOrCallback?: string | (() => void) | (() => Promise<void>),
    onOk?: (() => void) | (() => Promise<void>),
    onCancel?: () => void,
  ): Promise<boolean> {
    let title = 'Confirm'
    let okHandler: ((val?: any) => void) | undefined
    let cancelHandler = onCancel

    if (typeof titleOrCallback === 'function') {
      okHandler = titleOrCallback
      cancelHandler = onOk as (() => void) | undefined
    } else {
      if (titleOrCallback) title = titleOrCallback
      okHandler = onOk
    }

    return new Promise((resolve) => {
      currentDialog.value = {
        type: 'confirm',
        title,
        message,
        confirmText: 'OK',
        cancelText: 'Cancel',
        onOk: okHandler,
        onCancel: cancelHandler,
        resolve,
      }
      isOpen.value = true
    })
  }

  function alert(
    message: string,
    titleOrCallback?: string | (() => void) | (() => Promise<void>),
    onOk?: (() => void) | (() => Promise<void>),
  ): Promise<void> {
    let title = 'Alert'
    let okHandler: ((val?: any) => void) | undefined

    if (typeof titleOrCallback === 'function') {
      okHandler = titleOrCallback
    } else {
      if (titleOrCallback) title = titleOrCallback
      okHandler = onOk
    }

    return new Promise((resolve) => {
      currentDialog.value = {
        type: 'alert',
        title,
        message,
        confirmText: 'OK',
        onOk: okHandler,
        resolve,
      }
      isOpen.value = true
    })
  }

  function prompt(
    message: string,
    titleOrCallback?: string | ((val: string) => void),
    onOk?: ((val: string) => void) | (() => Promise<void>),
    onCancel?: () => void,
    defaultValue = '',
  ): Promise<string | null> {
    let title = 'Prompt'
    let okHandler: ((val: string) => void) | undefined
    let cancelHandler = onCancel

    if (typeof titleOrCallback === 'function') {
      okHandler = titleOrCallback
      cancelHandler = onOk as (() => void) | undefined
    } else {
      if (titleOrCallback) title = titleOrCallback
      okHandler = onOk as ((val: string) => void) | undefined
    }

    inputValue.value = defaultValue
    return new Promise((resolve) => {
      currentDialog.value = {
        type: 'prompt',
        title,
        message,
        confirmText: 'OK',
        cancelText: 'Cancel',
        defaultValue,
        onOk: okHandler,
        onCancel: cancelHandler,
        resolve,
      }
      isOpen.value = true
    })
  }

  function handleConfirm() {
    if (!currentDialog.value) return
    const dialog = currentDialog.value
    isOpen.value = false
    currentDialog.value = null
    if (dialog.type === 'prompt') {
      dialog.onOk?.(inputValue.value)
      dialog.resolve(inputValue.value)
    } else if (dialog.type === 'confirm') {
      dialog.onOk?.()
      dialog.resolve(true)
    } else {
      dialog.onOk?.()
      dialog.resolve(undefined)
    }
  }

  function handleCancel() {
    if (!currentDialog.value) return
    const dialog = currentDialog.value
    isOpen.value = false
    currentDialog.value = null
    dialog.onCancel?.()
    if (dialog.type === 'prompt') {
      dialog.resolve(null)
    } else {
      dialog.resolve(false)
    }
  }

  return {
    confirm,
    alert,
    prompt,
    handleConfirm,
    handleCancel,
    currentDialog,
    isOpen,
    inputValue,
  }
}
